import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from '@app/app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNgxMask } from 'ngx-mask';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import { PreLoaderStore } from '@app/components/header/store/pre-loader.store';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NgxsModule } from '@ngxs/store';
import { ApplicationState } from '@app/_state/app.state';
import { AuthConfig, OAuthModule } from 'angular-oauth2-oidc';
import { environment, firebaseConfig } from '../environments/environment';
import { AngularFireModule } from '@angular/fire/compat';
import { StoreModule } from '@ngrx/store';
import { metaReducers, reducers } from '@app/core/store';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { errorsInterceptor } from '@app/core/interceptors/errors.interceptor';
import { spinnerInterceptor } from '@app/core/interceptors/spinner.interceptor';
import { authInterceptor } from '@app/core/interceptors/auth.interceptor';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY'
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimationsAsync(),
    provideNgxMask(),
    provideHttpClient(
      withInterceptors([authInterceptor, spinnerInterceptor, errorsInterceptor])
    ),
    importProvidersFrom(
      NgxsModule.forRoot([ApplicationState]),
      OAuthModule.forRoot(),
      AngularFireModule.initializeApp(firebaseConfig.firebase),
      StoreModule.forRoot(reducers, { metaReducers })
    ),
    PreLoaderStore,
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => localStorage.getItem('currentLanguage') || 'uk'
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: false }
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        color: 'rgba(7, 255, 0, 0.9)'
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    },

    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) =>
          new TranslateHttpLoader(http, './assets/i18n/', '.json'),
        deps: [HttpClient]
      }
    })
  ]
};

export const restrictedPages = [
  '/signin',
  '/signup',
  '/resetpassword',
  '/notifications',
  '/main',
  '/company/details?tab=general',
  '/company/details?tab=registrationData',
  '/company/details?tab=contacts',
  '/company/details?tab=banksDetails',
  '/company/details?tab=youControl',
  '/procurement/tender-auction/tender-journal/',
  '/procurement/suppliers-office/procurement-list',
  '/company/details?tab=youControl',
  '/procurement/tender-auction/tender-journal/',
  '/procurement/suppliers-office/procurement-list',
  '/consulting/',
  '/consulting/as-is'
];

export const authConfig: AuthConfig = {
  issuer: environment.identityServer.issuer,
  redirectUri: window.location.origin,
  clientId: environment.identityServer.clientId,
  responseType: 'code',
  scope: 'openid profile email offline_access',
  showDebugInformation: true,
  requireHttps: false,
  tokenEndpoint: environment.identityServer.tokenEndpoint,
  timeoutFactor: 0.9,
  sessionChecksEnabled: true,
  silentRefreshRedirectUri: window.location.origin
};
